import useExperience from 'canvas_states/experience/useExperience';
import type { ECommerceEventData } from '../../../../../types/analytics/ECommerce';
import useAppConfig from 'canvas_states/config/useAppConfig';
import convertBidOptionToItem from './convertBidOptionToItem';
import convertToProductDetails from './convertToProductDetails';
import convertToECommerceEvent from './convertToECommerceEvent';
import type { PurchaseMode } from '../../../../../types/CoreShoppingExperience';
import type { OptionalItems } from '../../../../../types/analytics/AnalyticsEvent';

const useConvertToAddRemoveCartEvent = () => {
  const { experience } = useExperience().states;
  const partnerConfig = useAppConfig().states.config;

  const convertToAddRemoveCartEvent = (
    variantEncryptedId: string,
    quantity: number,
    textkeyGroup: string,
    upgradeType: string,
    amount: number,
    requestId: string,
    pointsAmount?: number,
    purchaseMode?: PurchaseMode,
    optionalItems?: OptionalItems,
    // eslint-disable-next-line max-params
  ): ECommerceEventData => {
    let product, variant;

    if (optionalItems?.isFallback) {
      const mappedFallbackVariants = experience.purchasableTrips
        .flatMap((trip) => trip.steps)
        .flatMap((step) => step.products)
        .flatMap((product) =>
          product.fallbackVariants?.map((variant) => {
            return { product, variant };
          }),
        );
      ({ product, variant } =
        mappedFallbackVariants.find((entry) => entry?.variant.encryptedId === variantEncryptedId) ?? {});
    } else {
      ({ product, variant } =
        experience.purchasableTrips
          .flatMap((trip) => trip.steps)
          .flatMap((step) => step.products)
          .flatMap((product) => [
            ...(product.variants || []).map((variant) => {
              return { product, variant };
            }),
            ...(product.fallbackVariants ?? []).map((variant) => {
              return { product, variant };
            }),
          ])
          .find(({ variant }) => variant.encryptedId === variantEncryptedId) ?? {});
    }

    if (product === undefined || variant === undefined) {
      throw new Error('Failed to find the related product or variant');
    }
    const ecommerceItemWithDetails = {
      ...convertBidOptionToItem(
        quantity,
        textkeyGroup,
        upgradeType,
        amount,
        variant,
        pointsAmount,
        purchaseMode,
        optionalItems,
      ),
      ...convertToProductDetails(product.productDetails),
    };

    return convertToECommerceEvent(requestId, [ecommerceItemWithDetails], partnerConfig);
  };

  return {
    actions: {
      convertToAddRemoveCartEvent,
    },
  };
};

export default useConvertToAddRemoveCartEvent;
